/**
 * Today Merch lead attribution bridge.
 *
 * Integration contract:
 * - Mark eligible forms with `data-today-merch-lead-form`.
 * - The CMP dispatches `todaymerch:google-consent-updated` on `document` with
 *   `detail.analytics_storage`, `detail.ad_storage`, `detail.ad_user_data` and
 *   `detail.ad_personalization` set to `granted` or `denied`. It also exposes
 *   its latest state as `window.todayMerchGoogleConsent`, allowing this module
 *   to recover consent when the event was dispatched before the module loaded.
 * - Product quotes dispatch `todaymerch:quote-succeeded` only after confirmed
 *   submission; general contacts dispatch `todaymerch:contact-succeeded`.
 *   `detail.lead_id` must be the value submitted in `contact[Lead ID]`.
 *   A confirmed `[data-today-merch-lead-success]` marker must explicitly name
 *   `quote_submitted` or `contact_submitted` for the reload-safe fallback.
 * - Booking code calls `TodayMerchLeadAttribution.notifyBookingComplete()`
 *   only after the booking API confirms the reservation, passing its opaque
 *   `eventId`. This ID deduplicates bookings independently of session lead IDs.
 * - The cart journey dispatches `today-merch:project-conversion` once per
 *   server-confirmed outcome: `project_submitted` (project received) or
 *   `project_card_authorized` (Stripe card or Link authorization). Each carries
 *   its own random `detail.lead_id`, the pre-tax amount in EUR when known and
 *   the payment method. They become primary marketing events of the same name.
 *
 * This module never loads a Google tag. Analytics consent routes the five
 * business events to the existing GA4 destination, without lead identifiers or
 * marketing attribution. Marketing consent separately controls Ads events and
 * attribution. GA4's automatic page_view and form_start remain with its tag.
 */
(() => {
  'use strict';

  const ATTRIBUTION_STORAGE_KEY = 'todaymerch_ads_attribution_v1';
  const EMITTED_STORAGE_KEY = 'todaymerch_ads_emitted_v1';
  const ANALYTICS_EMITTED_STORAGE_KEY = 'todaymerch_analytics_emitted_v1';
  const GA4_MEASUREMENT_ID = 'G-QMNSH7Q92H';
  const ANALYTICS_EVENTS = new Set([
    'quote_submitted', 'contact_submitted', 'booking_complete', 'click_to_call', 'add_to_quote',
  ]);
  const LEAD_ID_STORAGE_KEY = 'todaymerch_pending_lead_id_v1';
  const CONSENT_STORAGE_KEY = 'todaymerch_lead_consent_observation_v1';
  const CONSENT_EVENT = 'todaymerch:google-consent-updated';
  const QUOTE_SUCCESS_EVENT = 'todaymerch:quote-succeeded';
  const CONTACT_SUCCESS_EVENT = 'todaymerch:contact-succeeded';
  const BOOKING_COMPLETE_EVENT = 'todaymerch:booking-complete';
  const PROJECT_CONVERSION_EVENT = 'today-merch:project-conversion';
  const PROJECT_JOURNEY_SOURCE = 'today_merch_project_journey';
  const PROJECT_CONVERSIONS = new Set(['project_submitted', 'project_card_authorized', 'project_order_confirmed']);
  const PROJECT_PAYMENT_METHODS = new Set(['card', 'bank_transfer', 'undecided']);
  const MAX_CONVERSION_VALUE = 10000000;
  const PRODUCT_ADDED_EVENT = 'product-ajax:added';
  const FORM_SELECTOR = '[data-today-merch-lead-form]';
  const SUCCESS_SELECTOR = '[data-today-merch-lead-success]';
  const STORAGE_VERSION = 1;
  const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const MAX_EMITTED_KEYS = 100;
  const CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization'];
  const CONSENT_FIELDS = [
    ['contact[Consentement stockage publicitaire]', 'ad_storage'],
    ['contact[Consentement données publicitaires]', 'ad_user_data'],
    ['contact[Consentement personnalisation publicitaire]', 'ad_personalization'],
    ['contact[Consentement observé le]', 'captured_at'],
  ];

  const PARAMETER_DEFINITIONS = Object.freeze([
    { inputName: 'contact[GCLID]', key: 'gclid', maxLength: 512 },
    { inputName: 'contact[GBRAID]', key: 'gbraid', maxLength: 512 },
    { inputName: 'contact[WBRAID]', key: 'wbraid', maxLength: 512 },
    { inputName: 'contact[UTM source]', key: 'utm_source', maxLength: 256 },
    { inputName: 'contact[UTM medium]', key: 'utm_medium', maxLength: 256 },
    { inputName: 'contact[UTM campaign]', key: 'utm_campaign', maxLength: 256 },
    { inputName: 'contact[UTM term]', key: 'utm_term', maxLength: 256 },
    { inputName: 'contact[UTM content]', key: 'utm_content', maxLength: 256 },
  ]);

  const forms = new Set();
  const startedForms = new WeakMap();
  const pendingEvents = new Map();
  const emittedKeys = new Set();
  const pendingAnalyticsEvents = new Map();
  const analyticsEmittedKeys = new Set();
  let currentAttribution = {};
  let currentAttributionCapturedAt = null;
  let currentConsent = null;
  const unresolvedConsent = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    captured_at: new Date().toISOString(),
  };
  let currentLeadId = null;
  let marketingConsentGranted = false;
  let marketingConsentResolved = false;
  let analyticsConsentGranted = false;
  let analyticsConsentResolved = false;
  let pendingUrlAttribution = captureUrlAttribution();
  let pendingUrlCapturedAt = Date.now();
  let actionSequence = 0;
  const PAYMENT_REVOCATION_STORAGE = 'tm-payment-consent-withdrawal-v1';
  let revocationInFlight = null, appliedRevocation = '', authModuleLoading = null, pendingWithdrawal = {}, consentEpoch = 0, lastPrivacyChoice = '';

  function effectivePaymentConsent() {
    const privacy = window.TodayMerchPrivacyState, explicit = privacy?.ready === true && privacy?.recordedChoice === true;
    const analyticsRefused = explicit && (typeof privacy.analytics === 'boolean' ? !privacy.analytics : analyticsConsentResolved && !analyticsConsentGranted);
    const advertisingRefused = explicit && (typeof privacy.marketing === 'boolean' ? !privacy.marketing : marketingConsentResolved && !marketingConsentGranted);
    return { analytics: analyticsConsentGranted && !analyticsRefused, advertising: marketingConsentGranted && !advertisingRefused,
      analyticsRefused, advertisingRefused };
  }

  async function paymentAuth() {
    let hasSavedSession = false;
    try { hasSavedSession = Boolean(window.sessionStorage?.getItem('tm.customer-auth.session.v1')); } catch (_) {}
    if (!window.TodayMerchCustomerAuth && hasSavedSession && document.head) {
      authModuleLoading ||= new Promise(resolve => {
        const script = document.createElement('script');
        script.src = '/apps/todays-studio/project-assets/customer-auth.js'; script.async = true;
        const timer = setTimeout(() => resolve(), 2000);
        script.onload = script.onerror = () => { clearTimeout(timer); resolve(); };
        document.head.appendChild(script);
      });
      await authModuleLoading;
      if (!window.TodayMerchCustomerAuth) authModuleLoading = null;
    }
    return window.TodayMerchCustomerAuth?.getAccessToken?.();
  }

  // Necessary privacy operation, never an analytics event. This channel can only revoke.
  function syncPaymentMeasurementConsent({ force = false } = {}) {
    let pending = {};
    try { pending = JSON.parse(safeStorage('localStorage')?.getItem(PAYMENT_REVOCATION_STORAGE) || '{}') || {}; } catch (_) {}
    const consent = effectivePaymentConsent();
    const withdrawal = { analytics: pendingWithdrawal.analytics === true || pending.analytics === true || consent.analyticsRefused,
      advertising: pendingWithdrawal.advertising === true || pending.advertising === true || consent.advertisingRefused };
    if (!withdrawal.analytics && !withdrawal.advertising) return Promise.resolve(true);
    const key = JSON.stringify(withdrawal);
    if (!force && appliedRevocation === key) return Promise.resolve(true);
    pendingWithdrawal = withdrawal;
    writeStorageValue('localStorage', PAYMENT_REVOCATION_STORAGE, key);
    if (revocationInFlight) {
      if (!force) return revocationInFlight;
      // A fresh authenticated identity must not reuse an acknowledgement for the previous account.
      consentEpoch += 1; appliedRevocation = '';
      return revocationInFlight.then(() => syncPaymentMeasurementConsent());
    }
    if (typeof window.fetch !== 'function') return Promise.resolve(false);
    const requestEpoch = consentEpoch;
    revocationInFlight = (async () => {
      let authTimer;
      const token = await Promise.race([paymentAuth(), new Promise((_, reject) => { authTimer = setTimeout(() => reject(new Error('Privacy authentication unavailable')), 3000); })])
        .finally(() => clearTimeout(authTimer));
      const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      const timer = setTimeout(() => controller?.abort(), 3000);
      try {
        const headers = { Accept: 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
        const options = { method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers, ...(controller ? { signal: controller.signal } : {}) };
        const response = await window.fetch('/apps/todays-studio/project-api/session?purpose=personalization', options);
        if (!response.ok) return false;
        const session = await response.json();
        if (session.authenticated !== true || typeof session.csrfToken !== 'string' || !session.csrfToken) return false;
        // The two stores are independent: an unavailable bank endpoint must not
        // prevent the card withdrawal. Keep the pending intent until both confirm.
        const revoked = await Promise.allSettled(['authorizations', 'bank-transfers'].map(async store => {
          const response = await window.fetch('/apps/todays-studio/project-api/' + store + '/measurement-revocation', {
            ...options, method: 'POST', headers: { ...headers, 'Content-Type': 'application/json', 'X-Today-Merch-CSRF': session.csrfToken }, body: key,
          });
          return response.ok && (await response.json()).revoked === true;
        }));
        if (revoked.some(result => result.status !== 'fulfilled' || result.value !== true)) return false;
        if (requestEpoch === consentEpoch) appliedRevocation = key;
        if (requestEpoch === consentEpoch && JSON.stringify(pendingWithdrawal) === key) pendingWithdrawal = {};
        // Do not remove a stronger withdrawal that arrived during this request.
        if (requestEpoch === consentEpoch && safeStorage('localStorage')?.getItem(PAYMENT_REVOCATION_STORAGE) === key) removeStorageValue('localStorage', PAYMENT_REVOCATION_STORAGE);
        return true;
      } finally { clearTimeout(timer); }
    })().catch(() => false).then(accepted => {
      revocationInFlight = null;
      if (accepted && (pendingWithdrawal.analytics === true || pendingWithdrawal.advertising === true)) syncPaymentMeasurementConsent();
      return accepted;
    });
    return revocationInFlight;
  }

  function safeStorage(name) {
    try {
      const storage = window[name];
      if (!storage) return null;
      storage.getItem(ATTRIBUTION_STORAGE_KEY);
      return storage;
    } catch {
      return null;
    }
  }

  function removeStorageValue(storageName, key) {
    try {
      safeStorage(storageName)?.removeItem(key);
    } catch {
      // Storage can be blocked in private or hardened browser configurations.
    }
  }

  function writeStorageValue(storageName, key, value) {
    try {
      safeStorage(storageName)?.setItem(key, value);
    } catch {
      // Attribution must never break the lead form when storage is unavailable.
    }
  }

  function validAttributionValue(value, definition) {
    if (typeof value !== 'string' || value.length < 1 || value.length > definition.maxLength) return '';
    if (!definition.key.startsWith('utm_')) {
      return /^[A-Za-z0-9_-]{1,512}$/u.test(value) ? value : '';
    }
    // Match the commercial lead boundary before values reach browser storage or Shopify.
    return value === value.trim()
      && !/[\u0000-\u001f\u007f@]|:\/\/|www\./iu.test(value)
      && !/^\+?[\d\s().-]{7,}$/u.test(value)
      ? value : '';
  }

  function captureUrlAttribution() {
    const values = {};
    let url;

    try {
      url = new URL(window.location.href);
    } catch {
      return values;
    }

    for (const definition of PARAMETER_DEFINITIONS) {
      const value = validAttributionValue(
        url.searchParams.get(definition.key),
        definition,
      );

      if (value) values[definition.key] = value;
    }

    return values;
  }

  function validAttributionValues(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const normalized = {};
    for (const definition of PARAMETER_DEFINITIONS) {
      const candidate = validAttributionValue(value[definition.key], definition);
      if (candidate) normalized[definition.key] = candidate;
    }

    return normalized;
  }

  function readAttribution(storageName) {
    const storage = safeStorage(storageName);
    if (!storage) return {};

    const serialized = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!serialized) return {};

    try {
      const payload = JSON.parse(serialized);
      const values = validAttributionValues(payload?.values);
      const validPayload = payload?.version === STORAGE_VERSION
        && Number.isFinite(payload?.expiresAt)
        && payload.expiresAt > Date.now()
        && values;

      if (!validPayload) throw new Error('Invalid or expired attribution payload');
      // Older v1 records always used the fixed TTL even when capturedAt is absent.
      const capturedAt = Number.isFinite(payload.capturedAt)
        ? payload.capturedAt
        : payload.expiresAt - ATTRIBUTION_TTL_MS;
      if (capturedAt <= 0 || capturedAt > Date.now()) throw new Error('Invalid capture time');
      return { values, capturedAt };
    } catch {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }
  }

  function persistAttribution() {
    const values = validAttributionValues(currentAttribution) ?? {};
    if (Object.keys(values).length === 0) {
      removeStorageValue('sessionStorage', ATTRIBUTION_STORAGE_KEY);
      removeStorageValue('localStorage', ATTRIBUTION_STORAGE_KEY);
      return;
    }

    const capturedAt = currentAttributionCapturedAt;
    if (!Number.isFinite(capturedAt)) return;
    const payload = JSON.stringify({
      capturedAt,
      expiresAt: capturedAt + ATTRIBUTION_TTL_MS,
      values,
      version: STORAGE_VERSION,
    });
    writeStorageValue('sessionStorage', ATTRIBUTION_STORAGE_KEY, payload);
    writeStorageValue('localStorage', ATTRIBUTION_STORAGE_KEY, payload);
  }

  function restoreAttribution() {
    const localRecord = readAttribution('localStorage');
    const sessionRecord = readAttribution('sessionStorage');
    const storedRecord = sessionRecord.values ? sessionRecord : localRecord;
    currentAttributionCapturedAt = storedRecord.capturedAt ?? null;
    currentAttribution = {
      ...storedRecord.values,
    };
    mergeUrlAttribution();
    persistAttribution();
  }

  function mergeUrlAttribution() {
    if (!Object.keys(pendingUrlAttribution).length) return;
    const clickKeys = ['gclid', 'gbraid', 'wbraid'];
    const isNewClick = clickKeys.some((key) => (
      pendingUrlAttribution[key] && pendingUrlAttribution[key] !== currentAttribution[key]
    ));
    if (isNewClick) currentAttribution = {};
    if (isNewClick || !currentAttributionCapturedAt) {
      currentAttributionCapturedAt = pendingUrlCapturedAt;
    }
    currentAttribution = { ...currentAttribution, ...pendingUrlAttribution };
  }

  function observeConsent(detail) {
    const values = Object.fromEntries(CONSENT_KEYS.map((key) => (
      [key, detail?.[key] === 'granted' ? 'granted' : 'denied']
    )));
    if (currentConsent && CONSENT_KEYS.every((key) => currentConsent[key] === values[key])) {
      return currentConsent;
    }
    let previous = null;
    if (!currentConsent) {
      try {
        previous = JSON.parse(safeStorage('sessionStorage')?.getItem(CONSENT_STORAGE_KEY) ?? 'null');
      } catch {
        // Consent observation must not prevent a quote when storage is blocked.
      }
    }
    const previousTime = typeof previous?.captured_at === 'string' ? Date.parse(previous.captured_at) : NaN;
    const reusePrevious = previous && Number.isFinite(previousTime)
      && previousTime <= Date.now() && previousTime > Date.now() - ATTRIBUTION_TTL_MS
      && CONSENT_KEYS.every((key) => previous[key] === values[key]);
    currentConsent = {
      ...values,
      captured_at: reusePrevious ? previous.captured_at : new Date().toISOString(),
    };
    writeStorageValue('sessionStorage', CONSENT_STORAGE_KEY, JSON.stringify(currentConsent));
    return currentConsent;
  }

  function getSubmissionContext(form) {
    const formLeadId = form?.querySelector('input[name="contact[Lead ID]"]')?.value;
    const leadId = isLeadId(formLeadId) ? formLeadId : ensureLeadId();
    if (form && !isLeadId(formLeadId)) setHiddenValue(form, 'contact[Lead ID]', leadId);
    const attribution = marketingConsentGranted && Object.keys(currentAttribution).length
      ? { ...currentAttribution, captured_at: new Date(currentAttributionCapturedAt).toISOString() }
      : {};
    return {
      schema_version: 1,
      lead_id: leadId,
      consent: { ...(currentConsent ?? unresolvedConsent) },
      attribution,
    };
  }

  async function getPaymentMeasurementContext() {
    const identifiers = {};
    if (effectivePaymentConsent().analytics && typeof window.gtag === 'function') {
      await Promise.all(['client_id', 'session_id'].map(field => new Promise(resolve => {
        let finished = false;
        const finish = value => {
          if (finished) return;
          finished = true; clearTimeout(timer);
          if (effectivePaymentConsent().analytics && (field === 'client_id' ? /^[0-9]{1,20}\.[0-9]{1,20}$/ : /^[1-9][0-9]{0,15}$/).test(String(value || ''))) identifiers[field] = String(value);
          resolve();
        };
        const timer = setTimeout(() => finish(null), 250);
        try { window.gtag('get', GA4_MEASUREMENT_ID, field, finish); } catch { finish(null); }
      })));
    }
    const consent = effectivePaymentConsent();
    return {
      schema_version: 1, order_measurement_version: 1, captured_at: new Date().toISOString(),
      consent: { analytics_storage: consent.analytics ? 'granted' : 'denied',
        ...Object.fromEntries(CONSENT_KEYS.map(key => [key, consent.advertising ? 'granted' : 'denied'])) },
      attribution: consent.advertising && Object.keys(currentAttribution).length && currentAttributionCapturedAt
        ? { ...currentAttribution, captured_at: new Date(currentAttributionCapturedAt).toISOString() } : {},
      analytics: consent.analytics ? identifiers : {},
    };
  }

  function isLeadId(value) {
    return typeof value === 'string'
      && /^tm_lead_[0-9a-z-]{8,80}$/i.test(value);
  }

  function generateLeadId() {
    let uniquePart = '';

    try {
      uniquePart = window.crypto?.randomUUID?.() ?? '';
    } catch {
      uniquePart = '';
    }

    if (!uniquePart) {
      uniquePart = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    }

    return `tm_lead_${uniquePart}`;
  }

  function readPendingLeadId() {
    const sessionStorage = safeStorage('sessionStorage');
    let storedLeadId = null;

    try {
      storedLeadId = sessionStorage?.getItem(LEAD_ID_STORAGE_KEY) ?? null;
    } catch {
      storedLeadId = null;
    }

    return isLeadId(storedLeadId) ? storedLeadId : null;
  }

  function ensureLeadId() {
    if (isLeadId(currentLeadId)) return currentLeadId;

    currentLeadId = readPendingLeadId() ?? generateLeadId();
    writeStorageValue('sessionStorage', LEAD_ID_STORAGE_KEY, currentLeadId);
    return currentLeadId;
  }

  function setHiddenValue(form, name, value, attributionField = false) {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', name);
      if (attributionField) {
        input.setAttribute('data-today-merch-attribution-field', '');
      }
      form.appendChild(input);
    }

    input.value = value;
    return input;
  }

  function fillForm(form) {
    setHiddenValue(form, 'contact[Lead ID]', ensureLeadId());
    const context = getSubmissionContext(form);
    for (const [name, key] of CONSENT_FIELDS) {
      setHiddenValue(form, name, context.consent[key]);
    }
    setHiddenValue(form, 'contact[Attribution capturée le]', context.attribution.captured_at || '', true);

    if (!marketingConsentGranted) return;
    for (const definition of PARAMETER_DEFINITIONS) {
      const value = currentAttribution[definition.key];
      if (value) setHiddenValue(form, definition.inputName, value, true);
      else {
        const input = form.querySelector(`input[name="${definition.inputName}"]`);
        if (input) input.value = '';
      }
    }
  }

  function clearAttributionFields() {
    for (const form of forms) {
      for (const definition of PARAMETER_DEFINITIONS) {
        const input = form.querySelector(`input[name="${definition.inputName}"]`);
        if (input) input.value = '';
      }
      const captureField = form.querySelector('input[name="contact[Attribution capturée le]"]');
      if (captureField) captureField.value = '';
    }
  }

  function loadEmittedKeys(storageKey = EMITTED_STORAGE_KEY, targetKeys = emittedKeys) {
    const storage = safeStorage('sessionStorage');
    if (!storage) return;
    const serialized = storage.getItem(storageKey);
    if (!serialized) return;

    try {
      const keys = JSON.parse(serialized);
      if (!Array.isArray(keys)) throw new Error('Invalid emitted-event payload');

      for (const key of keys.slice(-MAX_EMITTED_KEYS)) {
        if (typeof key === 'string' && key.length <= 220) targetKeys.add(key);
      }
    } catch {
      storage.removeItem(storageKey);
    }
  }

  function persistEmittedKeys(storageKey = EMITTED_STORAGE_KEY, sourceKeys = emittedKeys) {
    const keys = Array.from(sourceKeys).slice(-MAX_EMITTED_KEYS);
    writeStorageValue('sessionStorage', storageKey, JSON.stringify(keys));
  }

  function analyticsEvent(eventName, conversionRole) {
    if (!effectivePaymentConsent().analytics || !ANALYTICS_EVENTS.has(eventName)) return false;

    const parameters = {
      send_to: GA4_MEASUREMENT_ID,
      event_source: 'today_merch_lead_attribution',
      conversion_role: conversionRole,
    };

    // Never copy submitted values, lead IDs or URL query strings to GA4.
    try {
      const url = new URL(window.location.href);
      parameters.page_location = `${url.origin}${url.pathname}`;
    } catch {
      parameters.page_location = 'https://todaymerch.com/';
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, parameters);
      } else {
        window.dataLayer = window.dataLayer || [];
        if (typeof window.dataLayer.push !== 'function') return false;
        // Use the gtag command format even when the existing tag loads later.
        (function queueGtagCommand() {
          window.dataLayer.push(arguments);
        })('event', eventName, parameters);
      }
      return true;
    } catch {
      // An unavailable analytics tag must never interrupt a successful form.
      return false;
    }
  }

  function emitAnalyticsOnce(eventName, conversionRole, leadId, deduplicationId = leadId) {
    if (!ANALYTICS_EVENTS.has(eventName)) return;
    const eventKey = `${eventName}:${deduplicationId}`;
    if (analyticsEmittedKeys.has(eventKey)) return;

    if (!analyticsConsentGranted) {
      if (!analyticsConsentResolved) {
        pendingAnalyticsEvents.set(eventKey, { eventName, conversionRole, leadId, deduplicationId });
      }
      return;
    }

    if (analyticsEvent(eventName, conversionRole)) {
      analyticsEmittedKeys.add(eventKey);
      pendingAnalyticsEvents.delete(eventKey);
      persistEmittedKeys(ANALYTICS_EMITTED_STORAGE_KEY, analyticsEmittedKeys);
    }
  }

  function dataLayerPush(payload) {
    if (!effectivePaymentConsent().advertising) return false;
    if (!window.dataLayer) window.dataLayer = [];
    if (typeof window.dataLayer.push !== 'function') return false;
    window.dataLayer.push(payload);
    try {
      document.dispatchEvent(new CustomEvent('todaymerch:lead-event', {
        detail: { ...payload },
      }));
    } catch {
      // Google Ads routing is best-effort and must never block the lead flow.
    }
    return true;
  }

  function buildPayload(eventName, conversionRole, leadId, extra = {}) {
    return {
      event: eventName,
      event_id: `${eventName}:${leadId}`,
      event_source: 'today_merch_lead_attribution',
      conversion_role: conversionRole,
      lead_id: leadId,
      ...currentAttribution,
      ...extra,
    };
  }

  function emitOnce(eventName, conversionRole, leadId, deduplicationId = leadId) {
    emitAnalyticsOnce(eventName, conversionRole, leadId, deduplicationId);
    emitMarketingOnce(eventName, conversionRole, leadId, deduplicationId);
  }

  function emitMarketingOnce(eventName, conversionRole, leadId, deduplicationId = leadId, extra = {}) {
    const eventKey = `${eventName}:${deduplicationId}`;
    if (emittedKeys.has(eventKey)) return;

    if (!marketingConsentGranted) {
      pendingEvents.set(eventKey, { conversionRole, eventName, leadId, deduplicationId, extra });
      return;
    }

    if (dataLayerPush(buildPayload(eventName, conversionRole, leadId, { ...extra, event_id: eventKey }))) {
      emittedKeys.add(eventKey);
      pendingEvents.delete(eventKey);
      persistEmittedKeys();
    }
  }

  function emitAction(eventName, conversionRole, extra = {}) {
    analyticsEvent(eventName, conversionRole);
    if (!marketingConsentGranted) return;
    actionSequence += 1;
    const leadId = ensureLeadId();
    const payload = buildPayload(eventName, conversionRole, leadId, extra);
    payload.event_id = `${eventName}:${leadId}:${Date.now()}:${actionSequence}`;
    dataLayerPush(payload);
  }

  function flushPendingEvents() {
    for (const pendingEvent of Array.from(pendingEvents.values())) {
      emitMarketingOnce(
        pendingEvent.eventName,
        pendingEvent.conversionRole,
        pendingEvent.leadId,
        pendingEvent.deduplicationId,
        pendingEvent.extra,
      );
    }
  }

  function registerForm(form) {
    if (!form || forms.has(form)) return;
    forms.add(form);
    fillForm(form);

    const markStarted = () => {
      if (startedForms.get(form)) return;
      startedForms.set(form, true);
      emitOnce('form_start', 'secondary', ensureLeadId());
    };

    form.addEventListener('input', markStarted);
    form.addEventListener('change', markStarted);
    form.addEventListener('submit', () => {
      const leadId = ensureLeadId();
      writeStorageValue('sessionStorage', LEAD_ID_STORAGE_KEY, leadId);
      fillForm(form);
    });
  }

  function consentGranted(detail) {
    return ['ad_storage', 'ad_user_data', 'ad_personalization'].every(
      (key) => detail?.[key] === 'granted',
    );
  }

  function handleConsent(event) {
    // A new choice is a new withdrawal epoch, including refuse → grant → refuse.
    appliedRevocation = '';
    consentEpoch += 1;
    observeConsent(event.detail);
    analyticsConsentResolved = true;
    analyticsConsentGranted = event.detail?.analytics_storage === 'granted';
    if (analyticsConsentGranted) {
      loadEmittedKeys(ANALYTICS_EMITTED_STORAGE_KEY, analyticsEmittedKeys);
      for (const pendingEvent of Array.from(pendingAnalyticsEvents.values())) {
        emitAnalyticsOnce(
          pendingEvent.eventName, pendingEvent.conversionRole, pendingEvent.leadId, pendingEvent.deduplicationId,
        );
      }
    } else {
      pendingAnalyticsEvents.clear();
      removeStorageValue('sessionStorage', ANALYTICS_EMITTED_STORAGE_KEY);
    }

    const wasMarketingConsentGranted = marketingConsentGranted;
    marketingConsentGranted = consentGranted(event.detail);
    marketingConsentResolved = true;
    syncPaymentMeasurementConsent();

    if (!marketingConsentGranted) {
      currentAttribution = {};
      currentAttributionCapturedAt = null;
      if (wasMarketingConsentGranted) pendingUrlAttribution = {};
      pendingEvents.clear();
      clearAttributionFields();
      removeStorageValue('sessionStorage', ATTRIBUTION_STORAGE_KEY);
      removeStorageValue('localStorage', ATTRIBUTION_STORAGE_KEY);
      removeStorageValue('sessionStorage', EMITTED_STORAGE_KEY);
      for (const form of forms) fillForm(form);
      return;
    }

    restoreAttribution();
    loadEmittedKeys();
    for (const form of forms) fillForm(form);
    flushPendingEvents();
  }

  function handleSubmissionSuccess(event, eventName, conversionRole) {
    const leadId = event.detail?.lead_id;
    if (!isLeadId(leadId)) return;
    emitOnce(eventName, conversionRole, leadId);
    removeStorageValue('sessionStorage', LEAD_ID_STORAGE_KEY);
    if (currentLeadId === leadId) currentLeadId = null;
  }

  function handleQuoteSuccess(event) {
    handleSubmissionSuccess(event, 'quote_submitted', 'primary');
  }

  function handleContactSuccess(event) {
    handleSubmissionSuccess(event, 'contact_submitted', 'secondary');
  }

  function isBookingEventId(value) {
    // Google Calendar event IDs are opaque lower-case base32hex identifiers.
    return typeof value === 'string' && /^[0-9a-v]{5,180}$/.test(value);
  }

  function handleBookingComplete(event) {
    const bookingId = event.detail?.eventId;
    if (!isBookingEventId(bookingId)) return;
    const providedLeadId = event.detail?.lead_id;
    const leadId = isLeadId(providedLeadId) ? providedLeadId : generateLeadId();
    emitOnce('booking_complete', 'secondary', leadId, `booking_${bookingId}`);
  }

  function projectConversionFields(detail) {
    const fields = {
      payment_method: detail.event === 'project_card_authorized' ? 'card'
        : PROJECT_PAYMENT_METHODS.has(detail.payment_method) ? detail.payment_method : 'undecided',
    };
    const { value } = detail;
    if (detail.currency === 'EUR' && typeof value === 'number' && Number.isFinite(value)
      && value >= 0 && value <= MAX_CONVERSION_VALUE) {
      fields.value = Math.round(value * 100) / 100;
      fields.currency = 'EUR';
    }
    return fields;
  }

  function handleProjectConversion(event) {
    const detail = event.detail;
    if (!PROJECT_CONVERSIONS.has(detail?.event) || detail.event_source !== PROJECT_JOURNEY_SOURCE) return;
    if (detail.event === 'project_order_confirmed' ? !/^tm_order_[a-f0-9]{32}$/.test(detail.lead_id || '') : !isLeadId(detail.lead_id)) return;
    // Wait only for a first choice: an outcome reached under a refusal is never replayed later.
    if (!effectivePaymentConsent().advertising && marketingConsentResolved) return;
    // Only these allowlisted cart facts are copied: never project, customer or server data.
    emitMarketingOnce(detail.event, 'primary', detail.lead_id, detail.lead_id, projectConversionFields(detail));
  }

  function handleProductAdded() {
    emitAction('add_to_quote', 'secondary');
  }

  function handleClick(event) {
    const telephoneLink = event.target?.closest?.('a[href^="tel:"]');
    if (!telephoneLink) return;
    emitAction('click_to_call', 'secondary');
  }

  function initialize() {
    document.addEventListener(CONSENT_EVENT, handleConsent);
    document.addEventListener(QUOTE_SUCCESS_EVENT, handleQuoteSuccess);
    document.addEventListener(CONTACT_SUCCESS_EVENT, handleContactSuccess);
    document.addEventListener(BOOKING_COMPLETE_EVENT, handleBookingComplete);
    document.addEventListener(PROJECT_CONVERSION_EVENT, handleProjectConversion);
    document.addEventListener(PRODUCT_ADDED_EVENT, handleProductAdded);
    // The CMP updates its recorded-choice flag just after the Google consent event.
    document.addEventListener('todaymerch:privacy-state', () => {
      const state = window.TodayMerchPrivacyState;
      const choice = JSON.stringify([state?.ready, state?.recordedChoice, state?.analytics, state?.marketing]);
      if (choice !== lastPrivacyChoice) { lastPrivacyChoice = choice; consentEpoch += 1; appliedRevocation = ''; }
      if (state?.ready === true && state?.recordedChoice === true) {
        if (state.analytics === false) {
          analyticsConsentGranted = false; analyticsConsentResolved = true; pendingAnalyticsEvents.clear();
          removeStorageValue('sessionStorage', ANALYTICS_EMITTED_STORAGE_KEY);
        }
        if (state.marketing === false) {
          marketingConsentGranted = false; marketingConsentResolved = true; pendingEvents.clear(); currentAttribution = {}; currentAttributionCapturedAt = null;
          currentConsent = { ...(currentConsent || unresolvedConsent), ...Object.fromEntries(CONSENT_KEYS.map(key => [key, 'denied'])) };
          clearAttributionFields(); removeStorageValue('sessionStorage', ATTRIBUTION_STORAGE_KEY); removeStorageValue('localStorage', ATTRIBUTION_STORAGE_KEY);
          removeStorageValue('sessionStorage', EMITTED_STORAGE_KEY);
        }
      }
      syncPaymentMeasurementConsent();
    });
    for (const name of ['pageshow', 'focus', 'online']) window.addEventListener?.(name, () => syncPaymentMeasurementConsent({ force: true }));
    document.addEventListener('click', handleClick);

    if (
      window.todayMerchGoogleConsent
      && typeof window.todayMerchGoogleConsent === 'object'
      && (
        consentGranted(window.todayMerchGoogleConsent)
        || window.todayMerchGoogleConsent.analytics_storage === 'granted'
        || (
          window.TodayMerchPrivacyState?.ready === true
          && window.TodayMerchPrivacyState?.recordedChoice === true
        )
      )
    ) {
      handleConsent({ detail: window.todayMerchGoogleConsent });
    }

    const successMarker = document.querySelector(SUCCESS_SELECTOR);
    const successType = successMarker?.getAttribute('data-today-merch-lead-success');
    if (successType === 'quote_submitted' || successType === 'contact_submitted') {
      const pendingLeadId = readPendingLeadId();
      if (pendingLeadId) {
        handleSubmissionSuccess(
          { detail: { lead_id: pendingLeadId } },
          successType,
          successType === 'quote_submitted' ? 'primary' : 'secondary',
        );
      }
      return;
    }

    for (const form of document.querySelectorAll(FORM_SELECTOR)) {
      registerForm(form);
    }
  }

  window.TodayMerchLeadAttribution = Object.freeze({
    capture() {
      pendingUrlAttribution = captureUrlAttribution();
      pendingUrlCapturedAt = Date.now();
      if (marketingConsentGranted) {
        mergeUrlAttribution();
        persistAttribution();
        for (const form of forms) fillForm(form);
      }
      return { ...pendingUrlAttribution };
    },
    events: Object.freeze({
      bookingComplete: BOOKING_COMPLETE_EVENT,
      contactSuccess: CONTACT_SUCCESS_EVENT,
      consent: CONSENT_EVENT,
      projectConversion: PROJECT_CONVERSION_EVENT,
      quoteSuccess: QUOTE_SUCCESS_EVENT,
    }),
    getAttribution: () => ({ ...currentAttribution }),
    getSubmissionContext,
    getPaymentMeasurementContext,
    syncPaymentMeasurementConsent,
    hasAnalyticsConsent: () => analyticsConsentGranted,
    hasMarketingConsent: () => marketingConsentGranted,
    notifyBookingComplete(detail = {}) {
      if (!isBookingEventId(detail.eventId)) return false;
      document.dispatchEvent(new CustomEvent(BOOKING_COMPLETE_EVENT, {
        detail: {
          eventId: detail.eventId,
          ...(isLeadId(detail.lead_id) ? { lead_id: detail.lead_id } : {}),
        },
      }));
      return true;
    },
    registerForm,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
